// Month calendar of cases by due date. Click a case to open, click a day to open the list for it.
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { WfCase } from '@alignova/shared';
import { useLiveEvent } from '../../lib/useLive.ts';
import { wfApi } from './workflowApi.ts';
import { Btn, PriorityPill, StagePill } from './wfUi.tsx';

const pad = (n: number) => String(n).padStart(2, '0');
const ymd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

export function WfCalendarPage() {
  const nav = useNavigate();
  const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [items, setItems] = useState<WfCase[]>([]);
  const [includeDone, setIncludeDone] = useState(false);
  const from = ymd(month);
  const to = ymd(new Date(month.getFullYear(), month.getMonth() + 1, 0));
  const load = () => { wfApi.list({ dueFrom: from, dueTo: to, limit: 500, includeTerminal: includeDone, sort: 'due', dir: 'asc' }).then((r) => setItems(r.items)).catch(() => undefined); };
  useEffect(load, [from, to, includeDone]);
  useLiveEvent('workflow.changed', load);
  const byDay = useMemo(() => { const m = new Map<string, WfCase[]>(); for (const c of items) if (c.dueDate) m.set(c.dueDate, [...(m.get(c.dueDate) ?? []), c]); return m; }, [items]);
  const first = new Date(month);
  const startPad = (first.getDay() + 6) % 7; // Monday first
  const days = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const cells: Array<Date | null> = [...Array(startPad).fill(null), ...Array.from({ length: days }, (_, i) => new Date(month.getFullYear(), month.getMonth(), i + 1))];
  while (cells.length % 7) cells.push(null);
  const today = ymd(new Date());
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Btn tone="ghost" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}>‹</Btn>
        <h1 className="text-xl font-semibold">{month.toLocaleString('en-GB', { month: 'long', year: 'numeric' })}</h1>
        <Btn tone="ghost" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}>›</Btn>
        <Btn tone="subtle" onClick={() => { const d = new Date(); setMonth(new Date(d.getFullYear(), d.getMonth(), 1)); }}>Today</Btn>
        <label className="ml-auto flex items-center gap-2 text-sm"><input type="checkbox" checked={includeDone} onChange={(e) => setIncludeDone(e.target.checked)} /> Include dispatched / cancelled</label>
        <span className="text-sm text-muted">{items.length} due this month</span>
      </div>
      <div className="grid grid-cols-7 gap-px overflow-hidden rounded-lg border border-line bg-line text-xs">
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => <div key={d} className="bg-surface-2 px-2 py-1 font-semibold uppercase text-muted">{d}</div>)}
        {cells.map((d, i) => {
          if (!d) return <div key={i} className="min-h-[110px] bg-surface/50" />;
          const key = ymd(d);
          const list = byDay.get(key) ?? [];
          const isToday = key === today;
          const past = key < today;
          return (
            <div key={key} className={`min-h-[110px] bg-surface p-1 ${isToday ? 'ring-2 ring-inset ring-accent' : ''}`}>
              <button onClick={() => nav(`/workflow/cases?dueFrom=${key}&dueTo=${key}${includeDone ? '&includeTerminal=1' : '&includeTerminal=0'}`)} className={`mb-1 block rounded px-1 font-semibold ${isToday ? 'bg-accent text-accent-fg' : past ? 'text-muted' : 'text-ink'} hover:underline`}>{d.getDate()}{list.length ? <span className="ml-1 font-mono text-[10px]">({list.length})</span> : ''}</button>
              <div className="space-y-0.5">
                {list.slice(0, 5).map((c) => (
                  <Link key={c.id} to={`/workflow/cases/${c.id}`} className={`block truncate rounded px-1 py-0.5 hover:bg-surface-2 ${c.isOverdue ? 'bg-status-urgent-bg/60 text-status-urgent-fg' : ''}`} title={`${c.patientName} · ${c.caseNumber}`}>
                    <PriorityPill p={c.priority} /> <b className="uppercase">{c.patientName}</b> <span className="text-muted">{c.groupId}</span> <StagePill stageKey={c.stageKey} small />
                  </Link>
                ))}
                {list.length > 5 && <button className="px-1 text-muted hover:underline" onClick={() => nav(`/workflow/cases?dueFrom=${key}&dueTo=${key}`)}>+{list.length - 5} more</button>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
